/**
 * Open a lead's thread when all you hold is the lead id.
 *
 * The Pipeline's cards and the Contacts table both know a `leadId` and neither knows the
 * conversation it belongs to — `/pipeline` and `/contacts` do not return one. The mock build got
 * away with deriving it (`l-anita` → `c-anita`); against the real API both are ULIDs with nothing
 * in common, so deriving one from the other navigates to a 404.
 *
 * So it is looked up: one `GET /leads/:id`, cached by react-query under the same key the lead 360
 * already uses, so the Inbox that opens next does not fetch it a second time.
 *
 * Author: Danish Khan
 */
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useNavigate } from 'react-router';
import { api } from './api.ts';

export type OpenLead = (leadId: string) => void;

export function useOpenLead(onError: (err: unknown) => void): OpenLead {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  return useCallback(
    (leadId: string) => {
      void queryClient
        .fetchQuery({ queryKey: ['lead', leadId], queryFn: () => api.lead(leadId) })
        .then((lead) => navigate(`/inbox/${lead.conversationId}`))
        .catch(onError);
    },
    [navigate, queryClient, onError],
  );
}
